/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useRef, useState } from "react";
import "../CSS/CalendarScreen.css";
import locationIcon from "../../../assets/images/calendar-location-icon.svg";
import eventVideoIcon from "../../../assets/images/event-video-icon.svg";
import exclusiveForYouIcon from "../../../assets/images/calendar-exclusive-for-you-button.svg";
import exclusiveIcon from "../../../assets/images/calendar-exclusive-button.svg.svg";
import placeholderGif from "../../../assets/gif/app-loader-reverse.gif";
import reserveSlotInfo from "../../../assets/images/reserve-slot-info-icon.svg";
import sepratorFirstImage from "../../../assets/images/calendar-seprator-calm.png";
import sepratorSecondImage from "../../../assets/images/calendar-seprator-thoughts.png";
import AppLoader from "components/elements/AppLoader";
import {
  API_DATE_FORMAT,
  CALENDAR_RESERVATION_TYPE,
  DATE_FORMAT,
  EXPERIENCE_TYPE,
  LaunchDateInnovativeSpace,
  LaunchDateMBStudio,
  LaunchDateMeetingRooms,
  LaunchDateRetreat,
  LaunchDateWorkspace,
  MEMBERSHIP_TYPE,
  MENU_ITEM,
  PURCHASE_TYPE,
} from "utils/constants";
import {
  checkNonMemberCredits,
  formatDateString,
  formatWorkspaceTimeRange,
  getDFWIcons,
  getIsActiveMemberStatus,
  getLaunchDateStatus,
  getWorkspaceSlotDuration,
  getWorkspaceSlotTime,
} from "utils/utils";
import dayjs from "dayjs";
import { useMutation, useQuery } from "@tanstack/react-query";
import useWSMApi from "services/api/useWSMApi";
import { useNavigate } from "react-router-dom";
import { useTabBar } from "services/TabBarProvider";
import ROUTE_PATHS from "utils/routePaths";
import { useStorage } from "services/StorageProvider";
import {
  trackEvent,
  trackReserveClick,
  trackSlotSelected,
} from "utils/analytics";
import ReserveAcknowledgementDialog from "components/elements/ReserveAcknowledgementDialog";
import BecomeMemberDialog from "components/elements/BecomeMemberDialog";
import InformationPopup from "components/elements/InformationPopup";
import { STRING } from "utils/strings";
import SnackbarAlert from "components/elements/SnackbarAlert";

interface CalendarTableContainerProps {
  selectedTab: string;
  selectedDate?: Date;
  showMonthView: boolean;
  passId?: string;
  experienceType?: string;
  onNoSlots?: (date: Date, tab: string) => void;
}

const CalendarTableContainer: FC<CalendarTableContainerProps> = ({
  selectedTab,
  selectedDate,
  showMonthView,
  passId,
  experienceType,
  onNoSlots,
}) => {
  const {
    fetchWSMSlots,
    reserveWSMSlot,
    fetchReservations,
    fetchUpcomingPasses,
    fetchExperiencePasses,
    sendEventReserveLead,
  } = useWSMApi();
  const navigate = useNavigate();
  const { setSelectedTabBar } = useTabBar();
  const [showAnimation, setShowAnimation] = useState(false);
  const { userProfileData, membershipListData } = useStorage();
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const isActiveMember = getIsActiveMemberStatus(userProfileData);
  const [showAcknowledgementDialog, setShowAcknowledgementDialog] =
    useState<boolean>(false);
  const [showBecomeMemberDialog, setShowBecomeMemberDialog] =
    useState<boolean>(false);
  const [showInformationPopup, setShowInformationPopup] =
    useState<boolean>(false);
  const imageIndexRef = useRef(0);
  const createLastRef = useRef<string>("");
  const innovateLastRef = useRef<string>("");
  const communicateLastRef = useRef<string>("");
  const experienceLastRef = useRef<string>("");
  const retreatLastRef = useRef<string>("");
  const wellnessLastRef = useRef<string>("");
  const workshopLastRef = useRef<string>("");

  const { data: slotsData, isLoading } = useQuery({
    queryKey: ["get-wsm-slots", selectedDate, selectedTab],
    queryFn: () =>
      selectedDate
        ? fetchWSMSlots({
          date: selectedDate
            ? dayjs(selectedDate).format(API_DATE_FORMAT)
            : "",
          type: selectedTab,
        }) : experienceType ?
          fetchExperiencePasses({ experience_type: experienceType })
          : fetchUpcomingPasses({ pass_id: passId }),
    retry: false,
  });

  const { data: reservationsData, isError: reservationError } = useQuery({
    queryKey: ["fetch-user-reservations"],
    queryFn: () => fetchReservations(),
    enabled: !!userProfileData,
  });

  const slotReservationMutation = useMutation({
    mutationFn: () => reserveWSMSlot({ slot_id: selectedSlot?.id }),
    onSuccess: () => {
      setShowAcknowledgementDialog(false);
      trackEvent("reservation_created", {
        slot_id: String(selectedSlot?.id ?? ""),
        service_type: selectedSlot?.service_type ?? "",
      });
      const paymentData = {
        title: `${selectedSlot?.title}`,
        type: PURCHASE_TYPE.EVENT,
      };
      localStorage.setItem("paymentSuccessData", JSON.stringify(paymentData));
      if (selectedSlot?.experience_type === EXPERIENCE_TYPE.FOCUS_HOUSE) {
        navigate(ROUTE_PATHS.WSM_CALENDAR_PASS_SUCCESS_SCREEN);
      } else {
        navigate(ROUTE_PATHS.WSM_ALL_SET);
      }
    },
  });

  const sendEventReserveLeadMutation = useMutation({
    mutationFn: (data: any) => sendEventReserveLead(data),
  });

  useEffect(() => {
    const timer = setTimeout(() => setShowAnimation(true), 500);
    return () => clearTimeout(timer);
  }, []);

  // When the query finishes and returns no slots, notify the parent so it can
  // jump to the next available date (daily view) or month (monthly view).
  useEffect(() => {
    if (
      !isLoading &&
      selectedDate &&
      onNoSlots &&
      slotsData?.data?.content?.length === 0
    ) {
      onNoSlots(selectedDate, selectedTab);
    }
  }, [isLoading, slotsData, selectedDate, selectedTab, showMonthView]);

  const getLoadingTitles = () => {
    switch (selectedTab) {
      case CALENDAR_RESERVATION_TYPE.DF_WORKSPACE:
        return "Warming up the Deep Focus Workstations 🔥";

      case CALENDAR_RESERVATION_TYPE.MB_STUDIO:
        return "Curating your wellness & work energy 💫";

      case CALENDAR_RESERVATION_TYPE.EVENTS:
        return "Charging creativity circuits ⚡";

      case CALENDAR_RESERVATION_TYPE.WORKSHOP:
        return "";

      case CALENDAR_RESERVATION_TYPE.MEETING_ROOMS:
        return "";

      default:
        return "";
    }
  };

  const isLaunchDatePassed = () => {
    const sDate = new Date(selectedDate ?? new Date());
    sDate.setHours(0, 0, 0, 0);
    return getLaunchDateStatus(sDate, selectedTab);
  };

  const handleBookingCardClick = (slot: any, reservation: any) => {
    localStorage.setItem(
      "selectedReservationData",
      JSON.stringify(reservation),
    );
    localStorage.setItem("selectedSlotData", JSON.stringify(slot));
    if (slot.service_type === CALENDAR_RESERVATION_TYPE.RETREAT) {
      navigate(ROUTE_PATHS.WSM_RETREAT_DETAIL.replace(":slotId", slot?.id));
    } else {
      navigate(ROUTE_PATHS.WSM_WORKSPACE_DETAIL.replace(":slotId", slot?.id));
    }
  };

  const getPlaceholderTitle = () => {
    const sDate = new Date(selectedDate ?? new Date());
    sDate.setHours(0, 0, 0, 0);
    const isSunday = sDate.getDay() === 0;
    if (isSunday) {
      return "Sorry, even The Foundry has Off Days!";
    }
    switch (selectedTab) {
      case CALENDAR_RESERVATION_TYPE.DF_WORKSPACE:
        return sDate < LaunchDateWorkspace
          ? "Create Space is coming soon 🌿 Reservations starts from 18th March."
          : "A free day - make it count to Scale Smarter Live better.";

      case CALENDAR_RESERVATION_TYPE.MB_STUDIO:
        return sDate < LaunchDateMBStudio
          ? "Wellness is coming soon 🌿 Reservations starts from 20th August."
          : "A free day - make it count to Scale Smarter Live better.";

      case CALENDAR_RESERVATION_TYPE.INNOVATE_SPACE:
        return sDate < LaunchDateInnovativeSpace
          ? "Innovate Space is coming soon 🌿 Reservations starts from 1st April."
          : "A free day - make it count to Scale Smarter Live better.";

      case CALENDAR_RESERVATION_TYPE.EVENTS:
        return "A free day - make it count to Scale Smarter Live better.";

      case CALENDAR_RESERVATION_TYPE.WORKSHOP:
        return sDate < LaunchDateWorkspace
          ? "WorkShop is coming soon 🌿 Reservations starts from 6th May."
          : "A free day - make it count to Scale Smarter Live better.";

      case CALENDAR_RESERVATION_TYPE.RETREAT:
        return sDate < LaunchDateRetreat
          ? "Our First Retreat is coming 🌿 Exact Details will be announced on 8th April."
          : "A free day - make it count to Scale Smarter Live better.";

      case CALENDAR_RESERVATION_TYPE.MEETING_ROOMS:
        return sDate < LaunchDateMeetingRooms
          ? "Communicate Space is coming soon 🌿 Reservations starts from 15th July."
          : "A free day - make it count to Scale Smarter Live better.";

      default:
        return "A free day - make it count to Scale Smarter Live better.";
    }
  };

  const handleRetreatReserveClick = (slot: any) => {
    setSelectedSlot(slot);
    localStorage.setItem("selectedSlotData", JSON.stringify(slot));

    trackSlotSelected(slot?.start_time ?? "", slot?.slot_date ?? "");
    trackReserveClick(String(slot?.id ?? ""));

    if (userProfileData) {
      const leadObj = {
        event_name: slot?.title,
        event_date: formatDateString(slot?.start_time),
        area: slot?.location,
        service_type: slot?.service_type,
        shift: getWorkspaceSlotDuration(slot?.start_time, slot?.end_time),
        timing: formatWorkspaceTimeRange(slot?.start_time, slot?.end_time),
        experience_type: slot?.experience_type,
        name: `${userProfileData.first_name ?? ""} ${userProfileData.last_name ?? ""}`,
        email: userProfileData.email ?? "",
        phone: userProfileData.mobile_number ?? "",
        profile_link: userProfileData.profile_link ?? "",
        is_founder: userProfileData.is_founder ? "True" : "False",
        role: userProfileData.role ?? "",
        is_member: isActiveMember ? "True" : "False",
        lookup_key: `${userProfileData.id}_${slot?.id}`
      };
      if (process.env.NODE_ENV === "production") {
        sendEventReserveLeadMutation.mutate(leadObj);
      }

      // Check for free Pass
      if (slot?.is_free_slot) {
        setShowAcknowledgementDialog(true);
        return;
      }

      if (isActiveMember) {
        const membershipEndDate = new Date(
          userProfileData?.credit?.membership_end_date,
        );
        const slotEndDate = new Date(slot.end_time);

        if (slotEndDate > membershipEndDate) {
          setShowInformationPopup(true);
          return;
        }
        setShowAcknowledgementDialog(true);
      } else {
        navigate(ROUTE_PATHS.WSM_CALENDAR_PAYMENT);
      }
    }
  };

  const getRetreatReserveButton = (slot: any) => {
    return (
      <div className="calendar-exclusive-slot-button-container">
        <div className="calendar-screen-reserve-tooltip-container">
          <div
            style={{
              backgroundImage: `url(${exclusiveIcon})`,
              cursor: isActiveMember ? "pointer" : "not-allowed",
            }}
            className="calendar-exclusive-slot-button "
            onClick={(event) => {
              if (!isActiveMember) return;
              event.stopPropagation();
              handleRetreatReserveClick(slot);
            }}
          ></div>
        </div>
      </div>
    );
  };

  const handleReservationConfirmClick = () => {
    slotReservationMutation.mutate();
  };

  const getExclusiveSlotReserveButton = (slot: any) => {
    const isAvailableForPurchase = isActiveMember
      ? slot?.purchase_access?.includes(userProfileData?.credit?.membership?.id)
      : false;

    let availableForMembershipName = null;

    if (slot?.purchase_access?.length > 0) {
      const availableForMembership = membershipListData?.data?.content?.find(
        (membership: any) => membership.id === slot?.purchase_access[0],
      );
      availableForMembershipName = MEMBERSHIP_TYPE.find(
        (type) => type.value === availableForMembership?.membership_type,
      )?.name;
    }

    return (
      <div className="calendar-exclusive-slot-button-container">
        <div className="calendar-screen-reserve-tooltip-container">
          <div
            style={{
              backgroundImage: `url(${isAvailableForPurchase ? exclusiveForYouIcon : exclusiveIcon
                })`,
              cursor: isAvailableForPurchase ? "pointer" : "not-allowed",
            }}
            className="calendar-exclusive-slot-button "
            onClick={(event) => {
              if (!isAvailableForPurchase) return;
              event.stopPropagation();
              handleReserveClick(slot);
            }}
          ></div>
          {availableForMembershipName && !isAvailableForPurchase && (
            <div className="calendar-screen-reserve-tooltip-text">
              <img src={reserveSlotInfo} alt="info-icon" />
              <div>
                <>
                  Available only for
                  <br />
                  {availableForMembershipName} membership.
                </>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getSepratorNextImage = () => {
    const images = [sepratorFirstImage, sepratorSecondImage];
    const image = images[imageIndexRef.current % images.length];
    imageIndexRef.current += 1;
    return image;
  };

  const getSepratorText = () => {
    const createStrings = [
      "Built for the work that starts in your mind.",
      "Use it when you need to think, read, write, plan, reflect",
      "A low lounge-style seating",
      "A library-inspired environment",
      "Come here when you need clarity before action.",
    ];

    const innovateStrings = [
      "Built for focused execution.",
      "When you need the right environment to stay focussed.",
      "Environment that Builds Momentum",
      "Execute without unnecessary noise.",
      "Ergonomic chairs, focused workstations, and a calm aesthetic",
      "Come here when your idea needs action.",
    ];

    const communicateStrings = [
      "Built for the work that needs people.",
      "Range of Privacy pods to small team rooms.",
      "When you need to meet, align, brainstorm, present, review, or make decisions.",
      "Privacy, tools, and setting for focussed & useful conversations.",
      "Where the work gets shared, sharpened, or decided.",
    ];

    const experiencesStrings = [
      "Curated experiences for founders, business owners.",
      "Its more than casual networking.",
      "Designed to help you meet the right people.",
      "Designed to protect your energy.",
      "Designed to build with more clarity.",
    ];

    const retreatStrings = ["A Performance retreat built for founders."];

    const wellnessStrings = [
      "Recovery engineered for high performers.",
      "Strength, mobility and real energy, under the guidance of an expert and guides.",
    ];

    const WorkshopsStrings = [
      "Focused, hands-on sessions that give founders practical skills and immediate tools to scale smarter.",
    ];

    const getNonRepeatingRandom = (
      arr: string[],
      lastRef: React.MutableRefObject<string>,
    ) => {
      if (arr.length === 1) return arr[0];

      let next = arr[Math.floor(Math.random() * arr.length)];

      while (next === lastRef.current) {
        next = arr[Math.floor(Math.random() * arr.length)];
      }

      lastRef.current = next;
      return next;
    };

    if (selectedTab === CALENDAR_RESERVATION_TYPE.DF_WORKSPACE) {
      return getNonRepeatingRandom(createStrings, createLastRef);
    } else if (selectedTab === CALENDAR_RESERVATION_TYPE.INNOVATE_SPACE) {
      return getNonRepeatingRandom(innovateStrings, innovateLastRef);
    } else if (selectedTab === CALENDAR_RESERVATION_TYPE.MEETING_ROOMS) {
      return getNonRepeatingRandom(communicateStrings, communicateLastRef);
    } else if (selectedTab === CALENDAR_RESERVATION_TYPE.EVENTS) {
      return getNonRepeatingRandom(experiencesStrings, experienceLastRef);
    } else if (selectedTab === CALENDAR_RESERVATION_TYPE.RETREAT) {
      return getNonRepeatingRandom(retreatStrings, retreatLastRef);
    } else if (selectedTab === CALENDAR_RESERVATION_TYPE.MB_STUDIO) {
      return getNonRepeatingRandom(wellnessStrings, wellnessLastRef);
    } else if (selectedTab === CALENDAR_RESERVATION_TYPE.WORKSHOP) {
      return getNonRepeatingRandom(WorkshopsStrings, workshopLastRef);
    }
  };

  const handleReserveClick = (slot: any) => {
    setSelectedSlot(slot);
    localStorage.setItem("selectedSlotData", JSON.stringify(slot));

    trackSlotSelected(slot?.start_time ?? "", slot?.slot_date ?? "");
    trackReserveClick(String(slot?.id ?? ""));

    if (userProfileData) {
      const leadObj = {
        event_name: slot?.title,
        event_date: formatDateString(slot?.start_time),
        area: slot?.location,
        service_type: slot?.service_type,
        shift: getWorkspaceSlotDuration(slot?.start_time, slot?.end_time),
        timing: formatWorkspaceTimeRange(slot?.start_time, slot?.end_time),
        experience_type: slot?.experience_type,
        name: `${userProfileData.first_name ?? ""} ${userProfileData.last_name ?? ""}`,
        email: userProfileData.email ?? "",
        phone: userProfileData.mobile_number ?? "",
        profile_link: userProfileData.profile_link ?? "",
        is_founder: userProfileData.is_founder ? "True" : "False",
        role: userProfileData.role ?? "",
        is_member: isActiveMember ? "True" : "False",
        lookup_key: `${userProfileData.id}_${slot?.id}`
      };
      if (process.env.NODE_ENV === "production") {
        sendEventReserveLeadMutation.mutate(leadObj);
      }

      // Check for free Pass
      if (slot?.is_free_slot) {
        setShowAcknowledgementDialog(true);
        return;
      }

      // Check membership validity for Reservation
      const membershipEndDate = new Date(
        userProfileData?.credit?.membership_end_date,
      );
      const slotEndDate = new Date(slot.end_time);

      if (slotEndDate > membershipEndDate && isActiveMember) {
        setShowInformationPopup(true);
        return;
      }

      // Handle Slot Reservation
      if (slot?.is_exclusive) {
        navigate(ROUTE_PATHS.WSM_CALENDAR_PAYMENT);
      } else if (
        isActiveMember &&
        checkNonMemberCredits(userProfileData?.credit, slot)
      ) {
        setShowAcknowledgementDialog(true);
      } else {
        navigate(ROUTE_PATHS.WSM_CALENDAR_PAYMENT);
      }
    }
  };

  return (
    <div>
      {slotReservationMutation?.isError ? (
        <SnackbarAlert type={"error"}>
          {slotReservationMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {isLoading ? (
        <AppLoader title={getLoadingTitles()} />
      ) : slotsData?.data?.content?.length > 0 && isLaunchDatePassed() ? (
        <div className="calendar-screen-main-container">
          <div className="calendar-slot-list-container">
            {slotsData?.data?.content?.map((slot: any, index: number) => {
              const reservation = !reservationError
                ? reservationsData?.data?.content?.find(
                  (reservation: any) =>
                    reservation?.service_slot_id === slot?.id,
                )
                : null;
              const isReserved = !!reservation;
              const slotTime = getWorkspaceSlotTime(slot?.start_time);
              const showSeparator = (index + 1) % 3 === 0;

              return (
                <div key={index}>
                  <div
                    className="calendar-slot-list-card"
                    onClick={() => handleBookingCardClick(slot, reservation)}
                  >
                    <div className="calendar-slot-info-main-container">
                      <div className="calendar-card-image-container">
                        <div className="calendar-card-time">{slotTime}</div>
                        <div
                          className="calendar-card-image"
                          style={
                            selectedTab ===
                              CALENDAR_RESERVATION_TYPE.DF_WORKSPACE ||
                              selectedTab ===
                              CALENDAR_RESERVATION_TYPE.MEETING_ROOMS
                              ? {
                                backgroundImage: `url(${getDFWIcons(
                                  slot?.start_time,
                                )})`,
                                backgroundColor: "black",
                                backgroundSize: "80% 80%",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "center",
                              }
                              : { backgroundImage: `url(${slot?.slot_image})` }
                          }
                        >
                          {slot?.event_type &&
                            slot?.service_type ===
                            CALENDAR_RESERVATION_TYPE.EVENTS && (
                              <div
                                className={`calendar-card-image-footer ${slot?.event_type === "IN_PERSON"
                                  ? "in-person"
                                  : "in-video"
                                  }`}
                              >
                                {slot?.event_type === "IN_PERSON" ? (
                                  "In Person"
                                ) : (
                                  <img
                                    src={eventVideoIcon}
                                    alt="calendar-in-person-icon"
                                  />
                                )}
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="calendar-card-info-container">
                        <div
                          className="calendar-card-title"
                          style={{ color: (showMonthView || experienceType) ? "#c1f700" : "white" }}
                        >
                          {experienceType ? dayjs(slot?.start_time).format(DATE_FORMAT) : slot?.title}
                        </div>
                        <div className="calendar-card-text-container">
                          <div style={{ width: "100%" }}>
                            {slot?.instructor && (
                              <div>{`${slot?.instructor?.first_name} ${slot?.instructor?.last_name}`}</div>
                            )}
                            <div className="calendar-card-location">
                              <img src={locationIcon} alt="location-logo" />
                              {slot?.location}
                            </div>
                            {showMonthView && (
                              <div>
                                {dayjs(slot?.start_time).format(DATE_FORMAT)}
                              </div>
                            )}
                            <div>
                              {getWorkspaceSlotDuration(
                                slot?.start_time,
                                slot?.end_time,
                              )}
                            </div>
                          </div>

                          {slot?.service_type ===
                            CALENDAR_RESERVATION_TYPE.RETREAT && !isReserved ? (
                            getRetreatReserveButton(slot)
                          ) : slot?.is_exclusive && !isReserved ? (
                            getExclusiveSlotReserveButton(slot)
                          ) : (
                            <button
                              className={`${isReserved
                                ? "calendar-slot-reserved-btn"
                                : "calendar-slot-reserve-btn"
                                }${showAnimation && index === 0 ? " animate" : ""
                                }`}
                              disabled={isReserved}
                              onClick={(event) => {
                                if (userProfileData) {
                                  event.stopPropagation();
                                  handleReserveClick(slot);
                                }
                              }}
                            >
                              {isReserved ? "Reserved ✓" : "Reserve"}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {showSeparator ? (
                    <div
                      className="calendar-slot-card-separator-container"
                      style={{
                        backgroundImage: `url(${getSepratorNextImage()})`,
                      }}
                    >
                      <span>{getSepratorText()}</span>
                    </div>
                  ) : (
                    <div className="calendar-slot-card-divider"></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="calendar-screen-placeholder-container">
          <img src={placeholderGif} alt="placeholder-icon" />
          <p>{getPlaceholderTitle()}</p>
        </div>
      )}

      <ReserveAcknowledgementDialog
        open={showAcknowledgementDialog}
        onClose={() => setShowAcknowledgementDialog(false)}
        slotData={selectedSlot}
        onClickLetsDo={handleReservationConfirmClick}
        isLoading={slotReservationMutation?.isPending}
      />
      <BecomeMemberDialog
        profileStatus={userProfileData?.credit?.status}
        open={showBecomeMemberDialog}
        onClose={() => setShowBecomeMemberDialog(false)}
      />
      <InformationPopup
        open={showInformationPopup}
        onClose={() => setShowInformationPopup(false)}
        onClick={() => {
          setShowInformationPopup(false);
          setSelectedTabBar(MENU_ITEM.MEMBERSHIP);
        }}
        title={STRING.MEMBERSHIP_EXPIRE_INFO_TITLE}
        subTitle={STRING.MEMBERSHIP_EXPIRE_INFO_SUB_TITLE}
        buttonText={STRING.MEMBERSHIP_EXPIRE_INFO_BUTTON_TITLE}
      />
    </div>
  );
};

export default CalendarTableContainer;
